import {
  Component, computed, DestroyRef, ElementRef, inject, Injector, Input, input, OnInit, output, ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { SimplePreloaderComponent } from '@shared/preloader/simple-preloader/simple-preloader.component';
import { GenericButtonComponent } from '@shared/ui';
import { NotificationService } from '@core/services';
import { DragDropDirective } from '@core/directives';
import { SafePipe } from '@core/base/layout';
import { FileUtil } from '@core/utils';
import { AssetType, FileAssetService } from '@core/entities/asset';
import { AssetUploadStore } from './assets-handler.store';

@Component({
    selector: 'assets-handler',
    templateUrl: './assets-handler.component.html',
    styleUrl: './assets-handler.component.scss',
    providers: [FileAssetService, AssetUploadStore],
    imports: [
        DragDropDirective,
        GenericButtonComponent,
        MatIconModule,
        SafePipe,
        SimplePreloaderComponent,
    ]
})
export class AssetsHandlerComponent implements OnInit {
  private notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  readonly MAX_NUMBER_OF_FILES = 20;
  readonly store = inject(AssetUploadStore);

  @Input() set accept(data: string | string[]) {
    this.store.setAcceptType(data);
  }
  @Input({ required: true }) set assetType(type: AssetType) {
    this.store.setAssetType(type);
  }

  readonly source = input<string | string[] | undefined>();

  readonly multiple = input<boolean>(false);
  readonly maxNumberOfFiles = computed(() => {
    return this.multiple() ? this.MAX_NUMBER_OF_FILES : 1;
  });
  readonly fileAddAllowed = computed(() => this.maxNumberOfFiles() > this.store.entities().length);

  readonly changed = output<string[]>();

  private readonly loadedUrls = computed(() =>
    this.store.entities()
      .filter(asset => asset.status === 'loaded' && asset.url)
      .map(asset => asset.url as string),
  );

  @ViewChild('fileInput') fileInput!: ElementRef;

  ngOnInit(): void {
    const source = this.source();
    const urls = Array.isArray(source) ? source : [source];

    this.store.initAssets(urls.filter(url => typeof url === 'string'));

    toObservable(this.loadedUrls, { injector: this.injector })
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(loaded => this.changed.emit(loaded));
  }

  protected addFiles(uploadedFiles: File[] | FileList | null): void {
    if (!uploadedFiles) {
      return;
    }

    if (!this.fileAddAllowed() || uploadedFiles.length > (this.maxNumberOfFiles() - this.store.entities().length)) {
      this.notificationService.showInfo(`The maximum number of files you can upload is ${this.maxNumberOfFiles()}`);
      return;
    }


    if (!this.store.assetType()) {
      throw new Error(`Missing asset type`);
    }

    const files = Array.from(uploadedFiles);
    const supportedExtensions = this.store.supportedExtensions();
    const invalidFileType = files?.some(file => !supportedExtensions.includes(FileUtil.getFileExtension(file.name))
    );
    if (invalidFileType) {
      this.notificationService.showError(`Only ${supportedExtensions} files are allowed`);
      throw new Error(`Only ${supportedExtensions} files are allowed`);
    }

    files.forEach(file => this.store.addAsset(file));
    this.clearFileInput();
  }

  protected removeFile(id: string): void {
    this.store.removeAsset(id);
  }

  private clearFileInput(): void {
    this.fileInput.nativeElement.value = '';
  }
}
